import axios from 'axios';

/**
 * BE(heviton-api: v2 + web-legacy/2.0) 에러 응답 정규화. 전역 `ExceptionAdvice`
 * (`extends ResponseEntityExceptionHandler`)가 모든 컨트롤러 예외를 RFC 7807 `ProblemDetail`
 * (`application/problem+json`)로 통일한다 — 경로 무관 단일 핸들러라 v2·2.0 동일 포맷.
 * Source: heviton-api `ProblemDetailFactory`/`ExceptionAdvice`.
 *
 * 인터셉터(패키지)는 HTTP status 라우팅만 하고 body 는 안 본다. 이 함수가 **유일한 본문 파싱 경계** —
 * 소비처(mutation onError 등)는 wire 포맷을 모른 채 내부 `ApiError` 만 읽는다(`fromRaw` 와 같은 경계 정규화).
 *
 * ⚠️ app-legacy(v3)는 별도 BE(app-user-api)라 포맷이 다름(`ApiResponse.meta`) — 여기서 다루지 않는다.
 */

/** RFC 7807 ProblemDetail wire 형태(우리가 읽는 필드만). 비표준 확장은 `properties` 단일 키 아래 중첩. */
interface ProblemDetail {
  title?: string;
  status?: number;
  detail?: string;
  properties?: {
    /** 레거시 `result.code` 호환(도메인은 status 보존 — business 아님). 로깅용. */
    code?: number;
    timestamp?: string;
    /** `@Valid @RequestBody` 검증 실패 — {필드: 메시지}. */
    body?: Record<string, string>;
  };
}

/**
 * FE 내부 표준 에러. 소비처는 이것만 읽는다(wire 포맷 무관).
 * 사용자 문구는 `title`→`UI_TEXT` 매핑 우선, `message`(BE detail)는 폴백/로깅용.
 */
export interface ApiError {
  /** HTTP status. 응답 없음(network/timeout/취소)이면 0. */
  status: number;
  /** BE `detail`(동적 메시지). 없으면 빈 문자열 — 소비처가 `UI_TEXT` 로 폴백. */
  message: string;
  /** 비즈니스 코드(`properties.code`). 로깅용. */
  code?: number;
  /** 도메인 식별자(`title` = `ProblemStatus.name`, 예 `NOT_FOUND_BILLING`) — UI 분기 키. */
  title?: string;
  /** 필드 검증 에러(`properties.body`). */
  fieldErrors?: Record<string, string>;
}

/** axios 에러를 내부 `ApiError` 로 정규화. 비-axios·응답없음·예기치 못한 body 는 generic 폴백. */
export function parseApiError(error: unknown): ApiError {
  if (!axios.isAxiosError<ProblemDetail>(error) || !error.response) {
    return { status: 0, message: '' };
  }
  const { status, data } = error.response;
  const problem = typeof data === 'object' && data !== null ? data : undefined;
  return {
    status,
    message: typeof problem?.detail === 'string' ? problem.detail : '',
    code: typeof problem?.properties?.code === 'number' ? problem.properties.code : undefined,
    title: typeof problem?.title === 'string' ? problem.title : undefined,
    fieldErrors: problem?.properties?.body,
  };
}
